import React from 'react';
import {
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Box,
  CircularProgress,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import { formatCurrency } from '../../utils/formatCurrency';
import { BEST_SELLERS_PERIOD_OPTIONS } from '../../utils/dashboardOverviewUtils';
import {
  DASHBOARD_COLORS,
  dashboardRowCardSx,
  sectionTitleSx,
  dashboardTableHeadCellSx,
  dashboardTableBodyCellSx,
} from './dashboardStyles';
import InsightAiAssistantButton from './ai/InsightAiAssistantButton';

const EMPTY_MESSAGES = {
  thisMonth: 'No sales this month yet.',
  lastMonth: 'No sales last month.',
};

const BestSellersTable = ({
  items,
  loading,
  period = 'thisMonth',
  onPeriodChange,
  showAiAssistant,
  onAiAssistantClick,
}) => (
  <Paper elevation={0} sx={dashboardRowCardSx}>
    <Box display="flex" justifyContent="space-between" alignItems="center" mb={2} flexWrap="wrap" gap={1}>
      <Typography sx={sectionTitleSx}>Best Selling Products</Typography>
      <Box display="flex" alignItems="center" gap={1}>
        {showAiAssistant && onAiAssistantClick && (
          <InsightAiAssistantButton onClick={onAiAssistantClick} />
        )}
        {onPeriodChange && (
          <ToggleButtonGroup
            exclusive
            size="small"
            value={period}
            disabled={loading}
            onChange={(_, next) => {
              if (next) onPeriodChange(next);
            }}
            sx={{
              bgcolor: DASHBOARD_COLORS.surface,
              border: `1px solid ${DASHBOARD_COLORS.border}`,
              borderRadius: 1.5,
              '& .MuiToggleButton-root': {
                textTransform: 'none',
                fontWeight: 600,
                fontSize: '0.75rem',
                px: 1.5,
                py: 0.35,
                border: 'none',
                color: DASHBOARD_COLORS.textMuted,
                '&.Mui-selected': {
                  bgcolor: '#eff6ff',
                  color: '#2563eb',
                },
              },
            }}
          >
            {BEST_SELLERS_PERIOD_OPTIONS.map((option) => (
              <ToggleButton key={option.value} value={option.value}>
                {option.label}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
        )}
      </Box>
    </Box>

    {loading ? (
      <Box flex={1} display="flex" alignItems="center" justifyContent="center" minHeight={180}>
        <CircularProgress size={28} />
      </Box>
    ) : items.length === 0 ? (
      <Box
        flex={1}
        display="flex"
        alignItems="center"
        justifyContent="center"
        sx={{
          border: `1px solid ${DASHBOARD_COLORS.border}`,
          borderRadius: 1.5,
          bgcolor: '#fafbfc',
          minHeight: 200,
          px: 2,
        }}
      >
        <Typography variant="body2" sx={{ color: DASHBOARD_COLORS.textMuted, textAlign: 'center' }}>
          {EMPTY_MESSAGES[period] || EMPTY_MESSAGES.thisMonth}
        </Typography>
      </Box>
    ) : (
      <TableContainer
        sx={{
          flex: 1,
          border: `1px solid ${DASHBOARD_COLORS.border}`,
          borderRadius: 1.5,
          overflow: 'hidden',
        }}
      >
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={dashboardTableHeadCellSx}>Product</TableCell>
              <TableCell align="right" sx={dashboardTableHeadCellSx}>
                Qty Sold
              </TableCell>
              <TableCell align="right" sx={dashboardTableHeadCellSx}>
                Revenue
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {items.map((row, index) => (
              <TableRow
                key={row.productId || row.productName}
                sx={{
                  '&:last-child td': { borderBottom: index === items.length - 1 ? 'none' : undefined },
                  '&:hover': { bgcolor: '#f9fafb' },
                }}
              >
                <TableCell sx={{ ...dashboardTableBodyCellSx, fontWeight: 500, maxWidth: 0 }}>
                  <Box component="span" sx={{ display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {row.productName}
                  </Box>
                </TableCell>
                <TableCell align="right" sx={{ ...dashboardTableBodyCellSx, fontWeight: 600 }}>
                  {row.quantitySold}
                </TableCell>
                <TableCell align="right" sx={{ ...dashboardTableBodyCellSx, fontWeight: 700, color: DASHBOARD_COLORS.text }}>
                  {formatCurrency(row.totalRevenue)}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    )}
  </Paper>
);

export default BestSellersTable;
